/**
 * The shared period library.
 *
 * Nearly every accommodation prices the same calendar — Easter, Ascension, the
 * May holiday, the summer weeks — and today those dates are retyped per rate,
 * per accommodation, per year. That is duplication with a real failure mode: one
 * of them gets a date wrong and nothing says so.
 *
 * ---------------------------------------------------------------------------
 * TWO KINDS OF ENTRY, AND WHY THEY ARE NOT THE SAME
 * ---------------------------------------------------------------------------
 *
 * **Computed public holidays** are exact. Ascension is Easter + 39 days whether
 * anyone likes it or not, so it is calculated, not stored, and is correct for
 * any year without anybody maintaining a table.
 *
 * **Park periods** are not, and reading the configs back shows why. `hemelvaart`
 * appears as many different ranges — but they are not competing opinions about
 * when Ascension is. They are TWO DIFFERENT WINDOWS doing two different jobs:
 *
 *   2026 day rates          13 - 18 May   (5 nights, the holiday)
 *   2026 arrangements        4 - 22 May  (18 nights, where the discount applies)
 *
 * The wide one exists so a long arrangement can still be used when most of its
 * nights are in the holiday and a few are not — thirteen high-season nights and
 * one low-season night. It can never win on a stay that is entirely low season,
 * because the low-season rates are cheaper outright, so widening it costs
 * nothing and buys flexibility. In practice the windows are set generously and
 * never trimmed, precisely because the cost of being too wide is zero — in that
 * direction. See `ladderLeakage()` for the direction where it is not.
 *
 * A `Period` therefore carries both: `from`/`until` for the holiday and
 * `applies` for the discount window.
 *
 * ---------------------------------------------------------------------------
 * COPY ON USE, NEVER A LIVE REFERENCE
 * ---------------------------------------------------------------------------
 *
 * Picking a period from the library COPIES its dates into the rate and records
 * where they came from. It does not link them.
 *
 * A live reference would mean editing the library silently repriced a published
 * version — including stays already quoted to guests. Versioning exists so a
 * published thing does not move; a live reference would undo that in one field.
 * `periodDrift()` gives back the useful half: it notices the library has changed
 * and lets the operator accept the update, which is the same accept-a-diff shape
 * used everywhere else here.
 */

import type { IsoDate } from './authoring';
import type { Rate } from './authoring';

export type PeriodKind =
  /** A date fixed by law or by the church calendar. Computed, never stored. */
  | 'publicHoliday'
  /** A school holiday. Set by government per region; cannot be computed. */
  | 'schoolHoliday'
  /** A season band the park defines: low, mid, high. */
  | 'season'
  /** A window the park has priced before, recovered from its own configs. */
  | 'parkPeriod';

export interface Period {
  id: string;
  label: string;
  kind: PeriodKind;
  year: number;
  /** Inclusive. The holiday itself — the nights a day rate prices. */
  from: IsoDate;
  /** Exclusive, matching every other range in this schema. */
  until: IsoDate;
  /**
   * Where a DISCOUNT belonging to this period may be placed.
   *
   * Strictly larger than the holiday, and this is not a quirk — it is how the
   * park has always priced. Ascension 2026 is a five-night window for day
   * rates (13-18 May) but arrangements carrying its discount are allowed over
   * eighteen (4-22 May), reaching into the cheaper weeks either side.
   *
   * The reason: a fourteen-night arrangement should still be usable when
   * thirteen of its nights are in the holiday and one is not. It can never win
   * where the whole stay is low season — the low-season rates are cheaper
   * outright — so widening the window costs nothing and buys flexibility.
   *
   * Conventionally the window extends into CHEAPER weeks only. Reaching into a
   * dearer period lets a discount priced off cheap nights undercut expensive
   * ones; `ladderLeakage()` in the generator looks for exactly that.
   *
   * Meaningless for the published ARI grid, where every cell already carries
   * its own price. This is a generator concept.
   */
  applies?: { from: IsoDate; until: IsoDate };
  source: 'computed' | 'park-history' | 'manual';
  /**
   * For park-history entries: how many accommodations used exactly this range,
   * and how many OTHER ranges share the name. A variant count above one is the
   * interesting case — it means the name does not pin a date.
   */
  usage?: { used: number; variants: number };
  note?: string;
}

const MS = 86_400_000;

export function isoOf(y: number, m: number, d: number): IsoDate {
  return new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
}

export function addDays(iso: IsoDate, days: number): IsoDate {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + days * MS).toISOString().slice(0, 10);
}

function weekdayOf(iso: IsoDate): number {
  return new Date(`${iso}T00:00:00Z`).getUTCDay(); // 0 = Sunday
}

/**
 * Easter Sunday, anonymous Gregorian algorithm.
 *
 * Worth computing rather than tabulating: it drives Good Friday, both Easter
 * days, Ascension and both Whit days, which between them are most of the park's
 * shoulder-season pricing. A table would be five entries per year, maintained
 * by hand, wrong the first year nobody updated it.
 */
export function easterSunday(year: number): IsoDate {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return isoOf(year, month, day);
}

/** One-day period helper: `from` inclusive, `until` the next day. */
function oneDay(id: string, label: string, year: number, from: IsoDate): Period {
  return {
    id: `${id}-${year}`, label, kind: 'publicHoliday', year,
    from, until: addDays(from, 1), source: 'computed',
  };
}

/**
 * Dutch public holidays for a year, exactly.
 *
 * Koningsdag carries a real rule rather than a fixed date: it is 27 April
 * unless that is a Sunday, when it moves to the 26th. Hard-coding the 27th is
 * wrong roughly one year in seven.
 */
export function dutchPublicHolidays(year: number): Period[] {
  const easter = easterSunday(year);
  const kingsBase = isoOf(year, 4, 27);
  const kings = weekdayOf(kingsBase) === 0 ? addDays(kingsBase, -1) : kingsBase;

  return [
    oneDay('nieuwjaarsdag', 'Nieuwjaarsdag', year, isoOf(year, 1, 1)),
    oneDay('goede-vrijdag', 'Goede Vrijdag', year, addDays(easter, -2)),
    oneDay('eerste-paasdag', 'Eerste Paasdag', year, easter),
    oneDay('tweede-paasdag', 'Tweede Paasdag', year, addDays(easter, 1)),
    oneDay('koningsdag', 'Koningsdag', year, kings),
    oneDay('bevrijdingsdag', 'Bevrijdingsdag', year, isoOf(year, 5, 5)),
    oneDay('hemelvaartsdag', 'Hemelvaartsdag', year, addDays(easter, 39)),
    oneDay('eerste-pinksterdag', 'Eerste Pinksterdag', year, addDays(easter, 49)),
    oneDay('tweede-pinksterdag', 'Tweede Pinksterdag', year, addDays(easter, 50)),
    oneDay('eerste-kerstdag', 'Eerste Kerstdag', year, isoOf(year, 12, 25)),
    oneDay('tweede-kerstdag', 'Tweede Kerstdag', year, isoOf(year, 12, 26)),
  ];
}

/**
 * The long weekends the park actually sells, derived from the public holidays.
 *
 * A holiday is one day; a stay is not. These are the windows a guest books
 * around them — Thursday to Sunday for Ascension, Friday to Monday for Whitsun
 * and Easter — offered as starting points an operator adjusts.
 */
export function dutchHolidayWeekends(year: number): Period[] {
  const easter = easterSunday(year);
  const ascension = addDays(easter, 39);
  const whit = addDays(easter, 49);
  return [
    {
      id: `paasweekend-${year}`, label: 'Paasweekend', kind: 'season', year,
      from: addDays(easter, -2), until: addDays(easter, 2), source: 'computed',
      note: 'Good Friday to the Tuesday after Easter Monday',
    },
    {
      id: `hemelvaartweekend-${year}`, label: 'Hemelvaartweekend', kind: 'season', year,
      from: ascension, until: addDays(ascension, 4), source: 'computed',
      note: 'Ascension Thursday to the following Sunday',
    },
    {
      id: `pinksterweekend-${year}`, label: 'Pinksterweekend', kind: 'season', year,
      from: addDays(whit, -2), until: addDays(whit, 2), source: 'computed',
      note: 'Saturday before Whit Sunday to the Tuesday after Whit Monday',
    },
  ];
}

/** Everything computable for a year, ready to seed a library. */
export function computedPeriods(year: number): Period[] {
  return [...dutchPublicHolidays(year), ...dutchHolidayWeekends(year)];
}

// ---------------------------------------------------------------------------
// Using a period
// ---------------------------------------------------------------------------

/**
 * Copy a period's dates onto a rate and record where they came from.
 *
 * A copy, not a link — see the note at the top of this file.
 *
 * WHICH window is copied depends on what the rate is. A nightly rate prices the
 * holiday itself and takes the narrow window. Anything carrying a discount —
 * a ladder step, an arrangement — takes the applicability window, because that
 * is the range over which the park has always allowed such a rate to be placed.
 */
export function applyPeriod(rate: Rate, period: Period): Rate {
  const window = periodWindowFor(rate, period);
  return {
    ...rate,
    active: { from: window.from, until: window.until },
    derivedFrom: period.id,
  };
}

/**
 * Which of a period's two windows a given rate would take.
 *
 * Split out of `applyPeriod` so a screen can SHOW the dates before copying
 * them. An operator picking "Hemelvaart" off a list and getting 4 – 22 May
 * where they expected 13 – 18 has been handed a bug to report; the same
 * operator reading "4 – 22 May" in the option they are about to choose has been
 * told how arrangements work. One rule, so the preview cannot promise a window
 * the copy then declines to use.
 */
export function periodWindowFor(
  rate: Pick<Rate, 'role' | 'nights'>, period: Period,
): { from: IsoDate; until: IsoDate } {
  const useApplies = period.applies !== undefined
    && rate.role !== 'nightly'
    && rate.nights > 1;
  return useApplies
    ? { from: period.applies!.from, until: period.applies!.until }
    : { from: period.from, until: period.until };
}

export interface PeriodProblem {
  periodId: string;
  code: 'applies-narrower' | 'empty-range';
  message: string;
}

/**
 * Structural checks on a library entry.
 *
 * The one that matters: an applicability window must CONTAIN the holiday. A
 * narrower one would make an arrangement unplaceable on the very days it was
 * written for, which is the opposite of what it is there to do.
 */
export function validatePeriod(p: Period): PeriodProblem[] {
  const out: PeriodProblem[] = [];
  if (p.until <= p.from) {
    out.push({
      periodId: p.id, code: 'empty-range',
      message: `${p.label} covers no nights (${p.from} to ${p.until})`,
    });
  }
  if (p.applies && (p.applies.from > p.from || p.applies.until < p.until)) {
    out.push({
      periodId: p.id,
      code: 'applies-narrower',
      message: `${p.label}: the window discounts may be placed in `
        + `(${p.applies.from} to ${p.applies.until}) does not contain the holiday itself `
        + `(${p.from} to ${p.until}), so an arrangement written for it could not be placed on it`,
    });
  }
  return out;
}

export interface Drift {
  rateId: string;
  periodId: string;
  periodLabel: string;
  rate: { from: IsoDate; until: IsoDate };
  library: { from: IsoDate; until: IsoDate };
}

/**
 * Rates whose dates no longer match the library period they came from.
 *
 * Either the library moved or someone adjusted the rate by hand; this does not
 * try to tell which, because both are legitimate and the operator knows. It
 * only surfaces the difference so the choice is theirs.
 */
export function periodDrift(rates: readonly Rate[], library: readonly Period[]): Drift[] {
  const byId = new Map(library.map((p) => [p.id, p]));
  const out: Drift[] = [];
  for (const r of rates) {
    if (!r.derivedFrom) continue;
    const p = byId.get(r.derivedFrom);
    if (!p) continue; // period retired from the library; not drift
    // A rate may legitimately carry either window, so it has drifted only if it
    // matches NEITHER. Comparing against one alone would report every
    // arrangement in the library as drifted.
    const holiday = { from: p.from, until: p.until };
    const applies = p.applies ?? holiday;
    const matches = (w: { from: IsoDate; until: IsoDate }) =>
      r.active.from === w.from && r.active.until === w.until;
    if (matches(holiday) || matches(applies)) continue;
    const expected = (r.role !== 'nightly' && r.nights > 1) ? applies : holiday;
    out.push({
      rateId: r.id,
      periodId: p.id,
      periodLabel: p.label,
      rate: { from: r.active.from, until: r.active.until },
      library: expected,
    });
  }
  return out;
}

/** Periods that cover a given date, for "what is happening on this night". */
export function periodsOn(date: IsoDate, library: readonly Period[]): Period[] {
  return library.filter((p) => date >= p.from && date < p.until);
}

/** Library entries for one year, ordered by start then length. */
export function forYear(library: readonly Period[], year: number): Period[] {
  return library
    .filter((p) => p.year === year)
    .sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : a.until.localeCompare(b.until)));
}

// ---------------------------------------------------------------------------
// Allowed and excluded periods
// ---------------------------------------------------------------------------

/**
 * A rate has ONE base window and a set of sub-windows it may be placed in.
 *
 * The base is `active`. The sub-windows are `placementWindows`, which is QPC's
 * `allowed_periods` under a clearer name and which the engine already enforces:
 * a placement must sit wholly inside one of them (`kinds.ts`). So "a base
 * period that must match first, then a list of periods it is allowed in" is
 * not a new idea to build — it is the shape the model already has, with
 * nothing on screen to drive it.
 *
 * EXCLUSION IS NOT A THIRD CONCEPT. Excluding Ascension from low season is the
 * same statement as allowing the two stretches either side of it, and the
 * engine can only be told one of those. Rather than store an intent and
 * recompile it — a second source of truth that drifts from the first — the
 * exclusion is performed HERE and the result is the windows themselves. What
 * the operator sees afterwards is what prices the stay.
 *
 * The cost is that "except Ascension" is not remembered as a phrase; the two
 * windows it produced are. That is a fair trade for a list an operator reads at
 * a glance, and the honest one: an intent that recompiles differently from what
 * was shown when it was chosen is the worse failure by a distance.
 */

type Window = { from: IsoDate; until: IsoDate };

/**
 * Merge overlapping and touching windows, dropping empty ones.
 *
 * Touching as well as overlapping: [1 May, 8 May) and [8 May, 15 May) admit
 * exactly the stays [1 May, 15 May) does, so leaving them apart would forbid a
 * fortnight for no reason anybody could see on screen.
 */
function normaliseWindows(windows: readonly Window[]): Window[] {
  const sorted = windows
    .filter((w) => w.from < w.until)
    .sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
  const out: Window[] = [];
  for (const w of sorted) {
    const last = out[out.length - 1];
    if (last && w.from <= last.until) {
      if (w.until > last.until) last.until = w.until;
    } else out.push({ from: w.from, until: w.until });
  }
  return out;
}

/** The windows a rate may be placed in today — its own, or its whole base. */
export function placementWindowsOf(rate: Rate): Window[] {
  if (rate.placementWindows?.length) {
    return normaliseWindows(rate.placementWindows.map((w) => ({ from: w.from, until: w.until })));
  }
  return [{ from: rate.active.from, until: rate.active.until }];
}

/**
 * Allow this rate to be placed on a period, in addition to wherever it already
 * may be placed.
 *
 * `null` when the period lies wholly outside the rate's base window, because
 * adding a window the base already forbids changes nothing and saying so is
 * more use than a button that appears to work.
 *
 * THE FIRST ONE NARROWS. A rate with no windows may be placed anywhere in its
 * base; adding one means "only here", not "the base and also here". That is
 * what `allowed_periods` has always meant, and it is the only reading that
 * makes the second, third and fourth additions consistent with the first.
 *
 * Clamped to the base, because the compiler warns about the part outside and
 * the engine ignores it. Storing a window that cannot be used is storing a
 * question for whoever reads it next.
 */
export function allowPeriod(rate: Rate, period: Period): Rate | null {
  const base = { from: rate.active.from, until: rate.active.until };
  const want = periodWindowFor(rate, period);
  const from = want.from > base.from ? want.from : base.from;
  const until = want.until < base.until ? want.until : base.until;
  if (from >= until) return null;
  const current = rate.placementWindows?.length
    ? rate.placementWindows.map((w) => ({ from: w.from, until: w.until }))
    : [];
  return { ...rate, placementWindows: normaliseWindows([...current, { from, until }]) };
}

/**
 * Forbid this rate from being placed over a period.
 *
 * ALWAYS THE HOLIDAY WINDOW, never `applies`. The wide window exists to say
 * where a discount MAY be placed; read backwards it would cut eighteen days out
 * of a season to keep a rate off a five-night holiday, which is not what anyone
 * excluding Ascension is asking for.
 *
 * `null` when nothing would be left. An empty `placementWindows` reads to the
 * engine as "no restriction" — the exact opposite of what was asked — so this
 * refuses rather than writing a field that means its own negation.
 *
 * The rate unchanged when the period does not overlap it at all.
 *
 * Note what this does and does not say. A stay must fit WHOLLY inside one
 * window, so excluding Ascension from the low-season rate stops that rate
 * covering any stay that spans Ascension — not just the holiday nights of it.
 * That is what "this rate is not for Ascension weeks" means. If the intent is
 * instead "those nights cost more", that is a dearer rate over the holiday, and
 * the cheapest tiling will find it without anything being forbidden.
 */
export function excludePeriod(rate: Rate, period: Period): Rate | null {
  const cut = { from: period.from, until: period.until };
  const current = placementWindowsOf(rate);
  const kept: Window[] = [];
  for (const w of current) {
    if (cut.until <= w.from || cut.from >= w.until) { kept.push(w); continue; }
    if (cut.from > w.from) kept.push({ from: w.from, until: cut.from });
    if (cut.until < w.until) kept.push({ from: cut.until, until: w.until });
  }
  const next = normaliseWindows(kept);
  if (next.length === 0) return null;
  if (!rate.placementWindows?.length && next.length === 1
    && next[0]!.from === rate.active.from && next[0]!.until === rate.active.until) {
    // Nothing was cut, so do not add a field that only restates the base.
    return rate;
  }
  return { ...rate, placementWindows: next };
}

/** Drop one placement window; removing the last one restores the whole base. */
export function removePlacementWindow(rate: Rate, index: number): Rate {
  const current = rate.placementWindows ?? [];
  const next = current.filter((_, i) => i !== index);
  const out = { ...rate };
  if (next.length === 0) delete (out as { placementWindows?: unknown }).placementWindows;
  else out.placementWindows = next;
  return out;
}
